import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Card, Col, DatePicker, Divider, Drawer, Dropdown, Form, Input, InputNumber, Row, Segmented, Select, Space, Spin, Switch, Typography, type InputRef } from 'antd';
import { EyeOutlined, SendOutlined, TagOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { STEP_CONDITIONS, STEP_CONDITION_LABELS, emptyForward, htmlHasText, replySubject, type SequenceItem, type SequenceStep, type StepCondition } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { ContactSelect } from '../../components/ContactSelect';
import { useMergeTags } from '../../features/email/api';
import { useStepPreview, useStepTest } from '../../features/sequences/api';
import { useAuth } from '../../auth/AuthProvider';
import { errorMessage } from '../../api/client';
import { ActionsEditor, actionsComplete } from './ActionsEditor';

export const STEP_TYPE_LABEL = { EMAIL: 'Automatic email', TASK: 'Manual task', ACTION: 'CRM action' } as const;

interface Props {
  open: boolean;
  sequence: SequenceItem;
  /** the step being edited, and where it sits */
  step: SequenceStep | null;
  index: number;
  /** the steps before this one (for "first email" rules and the reply subject) */
  before: SequenceStep[];
  onClose: () => void;
  onSave: (step: SequenceStep) => void;
}

/** Subject input with a merge-tag menu that inserts at the cursor. */
function TagInput({ value, onChange, placeholder, ariaLabel }: { value: string; onChange: (v: string) => void; placeholder?: string; ariaLabel: string }) {
  const tags = useMergeTags();
  const ref = useRef<InputRef>(null);
  const insert = (tag: string) => {
    const el = ref.current?.input;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    onChange(`${value.slice(0, at)}{{${tag}}}${value.slice(end)}`);
  };
  return (
    <Input
      ref={ref}
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      maxLength={500}
      onChange={(e) => onChange(e.target.value)}
      suffix={
        <Dropdown trigger={['click']} menu={{ items: (tags.data ?? []).map((t) => ({ key: t.tag, label: t.label })), onClick: ({ key }) => insert(key), style: { maxHeight: 'min(360px, 50vh)', overflowY: 'auto' } }} placement="bottomRight">
          <Button type="text" size="small" icon={<TagOutlined />} aria-label="Insert merge tag" />
        </Dropdown>
      }
    />
  );
}

/** Sandboxed render of the email html, like a mail client shows it. */
function MailBody({ html }: { html: string }) {
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;padding:12px 4px;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.45;color:#222}a{color:#1155cc}blockquote{margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex}p{margin:0}</style></head><body>${html}</body></html>`;
  return <iframe title="Email preview" sandbox="allow-popups" srcDoc={doc} style={{ width: '100%', minHeight: 360, border: 0, background: '#fff' }} />;
}

export function StepDrawer({ open, sequence, step, index, before, onClose, onSave }: Props) {
  const { message } = App.useApp();
  const { user } = useAuth();
  const [draft, setDraft] = useState<SequenceStep | null>(step);
  const [previewStep, setPreviewStep] = useState<SequenceStep | null>(step);
  const [contactId, setContactId] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const test = useStepTest();

  useEffect(() => {
    setDraft(step);
    setPreviewStep(step);
  }, [step]);
  useEffect(() => {
    if (!testTo && user?.email) setTestTo(user.email);
  }, [user?.email, testTo]);
  // re-render the preview a moment after typing stops
  useEffect(() => {
    const t = window.setTimeout(() => setPreviewStep(draft), 600);
    return () => window.clearTimeout(t);
  }, [draft]);
  const isEmail = draft?.type === 'EMAIL';
  const preview = useStepPreview(open && isEmail ? sequence.id : undefined, previewStep, contactId, agentId);

  if (!draft) return null;
  const set = (patch: Partial<SequenceStep>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const fwd = draft.forward ?? emptyForward();
  const setFwd = (patch: Partial<typeof fwd>) => set({ forward: { ...fwd, ...patch } });
  const firstEmail = isEmail && !before.some((s) => s.type === 'EMAIL');
  const openingSubject = before.find((s) => s.type === 'EMAIL')?.subject ?? '';
  const inThread = isEmail && !firstEmail && draft.threadMode === 'reply' && !fwd.enabled;
  const wait = index === 0 ? 'after the person is added' : 'after the previous step';

  const problem = (() => {
    if (isEmail) {
      if (!htmlHasText(draft.html) && !fwd.enabled) return 'Write the email.';
      if (!inThread && !draft.subject.trim() && !(fwd.enabled && fwd.subject.trim())) return 'Add a subject.';
      if (fwd.enabled && (!fwd.fromName.trim() && !fwd.fromEmail.trim())) return 'Fill in who the forwarded email is from.';
      return null;
    }
    if (!draft.actions.length) return 'Add at least one action.';
    if (!actionsComplete(draft.actions)) return 'Finish the actions (pick a tag, list, value or task title).';
    return null;
  })();

  const save = () => {
    if (problem) return message.warning(problem);
    onSave(firstEmail || fwd.enabled ? { ...draft, threadMode: 'new' } : draft);
  };

  const sendTest = () => {
    if (!sequence.agents.length) return message.warning('Pick an agent under Settings first');
    test
      .mutateAsync({ id: sequence.id, step: draft, to: testTo.trim(), contactId, agentId })
      .then((r) => message.success(r.delivered ? `Test sent to ${testTo}` : `Rendered "${r.subject}" (no mail provider configured, so nothing was sent)`))
      .catch((e) => message.error(errorMessage(e)));
  };

  return (
    <Drawer
      title={`Step ${index + 1} · ${STEP_TYPE_LABEL[draft.type]}`}
      open={open}
      onClose={onClose}
      size={isEmail ? 1180 : 640}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={save}>Done</Button>
        </Space>
      }
    >
      <Row gutter={24}>
        <Col xs={24} lg={isEmail ? 13 : 24}>
          <Form layout="vertical" component="div">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label="Wait" extra={wait}>
                  <Space.Compact>
                    <InputNumber aria-label="Wait days" min={0} max={365} value={draft.delayDays} onChange={(v) => set({ delayDays: v ?? 0 })} style={{ width: 90 }} suffix="days" />
                    <InputNumber aria-label="Wait hours" min={0} max={23} value={draft.delayHours} onChange={(v) => set({ delayHours: v ?? 0 })} style={{ width: 90 }} suffix="hrs" />
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Run" extra={index === 0 ? 'Nothing has been sent before the first step, so it always runs.' : 'Steps whose condition fails are skipped.'}>
                  <Select<StepCondition> aria-label="Condition" disabled={index === 0} value={draft.condition} onChange={(v) => set({ condition: v })} options={STEP_CONDITIONS.map((c) => ({ value: c, label: STEP_CONDITION_LABELS[c] }))} />
                </Form.Item>
              </Col>
            </Row>

            {isEmail ? (
              <>
                {!firstEmail && !fwd.enabled && (
                  <Form.Item label="Send as">
                    <Segmented
                      value={draft.threadMode}
                      onChange={(v) => set({ threadMode: v as SequenceStep['threadMode'] })}
                      options={[
                        { value: 'reply', label: 'Reply in the same thread' },
                        { value: 'new', label: 'New email' },
                      ]}
                    />
                  </Form.Item>
                )}
                <Form.Item label="Subject" extra={inThread ? 'Follow-ups in the thread keep the first subject, like a real reply.' : 'Short and lower-key works best, e.g. “quick question, {{first_name}}”.'}>
                  {inThread ? <Input aria-label="Subject" disabled value={replySubject(openingSubject || '…')} /> : <TagInput ariaLabel="Subject" value={draft.subject} onChange={(v) => set({ subject: v })} placeholder={fwd.enabled ? `Fwd: ${fwd.subject || '…'}` : 'Subject'} />}
                </Form.Item>
                <Form.Item label={fwd.enabled ? 'Your note above the forwarded email' : 'Email'} extra={<>Write it like a personal email: no images or buttons. <Typography.Text code>{'{Hi|Hey|Hello}'}</Typography.Text> picks one at random for each person.</>}>
                  <RichTextEditor value={draft.html} onChange={(html) => set({ html })} minHeight={200} placeholder="Hi {{first_name}}, …" />
                </Form.Item>
                <Space wrap size="large" style={{ marginBottom: 16 }}>
                  <Space><Switch aria-label="Include the agent's signature" checked={draft.includeSignature} onChange={(v) => set({ includeSignature: v })} /><Typography.Text>Agent's signature</Typography.Text></Space>
                  {inThread && !fwd.enabled && (
                    <Space><Switch aria-label="Quote the previous email" checked={draft.quotePrevious} onChange={(v) => set({ quotePrevious: v })} /><Typography.Text>Quote the previous email below</Typography.Text></Space>
                  )}
                  <Space><Switch aria-label="Send as a forwarded email" checked={fwd.enabled} onChange={(v) => setFwd({ enabled: v })} /><Typography.Text>Send as a forwarded email</Typography.Text></Space>
                </Space>

                {fwd.enabled && (
                  <Card size="small" title="Forwarded email" style={{ marginBottom: 16 }} styles={{ body: { paddingBottom: 4 } }}>
                    <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
                      Shown under “---------- Forwarded message ---------” exactly as Gmail does. Every field takes merge tags.
                    </Typography.Paragraph>
                    <Row gutter={12}>
                      <Col span={12}><Form.Item label="From name"><TagInput ariaLabel="Forwarded from name" value={fwd.fromName} onChange={(v) => setFwd({ fromName: v })} placeholder="Jane Doe" /></Form.Item></Col>
                      <Col span={12}><Form.Item label="From email"><TagInput ariaLabel="Forwarded from email" value={fwd.fromEmail} onChange={(v) => setFwd({ fromEmail: v })} placeholder="jane@company.com" /></Form.Item></Col>
                      <Col span={12}><Form.Item label="To name"><TagInput ariaLabel="Forwarded to name" value={fwd.toName} onChange={(v) => setFwd({ toName: v })} placeholder="{{agent_name}}" /></Form.Item></Col>
                      <Col span={12}><Form.Item label="To email"><TagInput ariaLabel="Forwarded to email" value={fwd.toEmail} onChange={(v) => setFwd({ toEmail: v })} placeholder="{{agent_email}}" /></Form.Item></Col>
                      <Col span={24}><Form.Item label="Cc (optional)"><TagInput ariaLabel="Forwarded cc" value={fwd.cc} onChange={(v) => setFwd({ cc: v })} placeholder="Name <email>, …" /></Form.Item></Col>
                      <Col span={24}>
                        <Form.Item label="Date">
                          <Space wrap>
                            <Segmented value={fwd.dateMode} onChange={(v) => setFwd({ dateMode: v as typeof fwd.dateMode })} options={[{ value: 'relative', label: 'Days before sending' }, { value: 'fixed', label: 'Fixed date' }]} />
                            {fwd.dateMode === 'relative' ? (
                              <InputNumber aria-label="Days before sending" min={0} max={365} value={fwd.daysBefore} onChange={(v) => setFwd({ daysBefore: v ?? 0 })} suffix="days earlier" style={{ width: 160 }} />
                            ) : (
                              <DatePicker aria-label="Forwarded date" showTime={{ format: 'HH:mm' }} format="ddd, MMM D, YYYY h:mm A" value={fwd.date ? dayjs(fwd.date) : null} onChange={(d) => setFwd({ date: d ? d.toISOString() : null })} />
                            )}
                          </Space>
                        </Form.Item>
                      </Col>
                      <Col span={24}><Form.Item label="Original subject"><TagInput ariaLabel="Forwarded subject" value={fwd.subject} onChange={(v) => setFwd({ subject: v })} placeholder="Re: Results after the pilot" /></Form.Item></Col>
                      <Col span={24}>
                        <Form.Item label="Original message">
                          <RichTextEditor value={fwd.html} onChange={(html) => setFwd({ html })} minHeight={140} placeholder="The email being forwarded…" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                )}
              </>
            ) : (
              <Form.Item
                label={draft.type === 'TASK' ? 'Task for your team' : 'Changes to make in the CRM'}
                extra={draft.type === 'TASK' ? 'Creates a task on the contact (a call, a LinkedIn message…). The sequence moves on without waiting for it.' : 'Runs on its own when the step comes up.'}
              >
                <ActionsEditor value={draft.actions} onChange={(actions) => set({ actions })} types={draft.type === 'TASK' ? ['create_task'] : undefined} addLabel={draft.type === 'TASK' ? 'Add task' : 'Add action'} />
              </Form.Item>
            )}
          </Form>
        </Col>

        {isEmail && (
          <Col xs={24} lg={11}>
            <div style={{ position: 'sticky', top: 0 }}>
              <Space style={{ marginBottom: 8, width: '100%' }} wrap>
                <EyeOutlined />
                <Typography.Text strong>Preview as</Typography.Text>
                <div style={{ minWidth: 220 }}><ContactSelect value={contactId} onChange={(v) => setContactId(v)} quickCreate={false} /></div>
                {sequence.agents.length > 1 && (
                  <Select aria-label="Preview agent" allowClear placeholder="Agent" style={{ width: 160 }} value={agentId ?? undefined} onChange={(v) => setAgentId(v ?? null)} options={sequence.agents.map((a) => ({ value: a.id, label: a.name }))} />
                )}
              </Space>
              <Card size="small" styles={{ body: { padding: 0 } }}>
                {preview.isError ? (
                  <Alert type="error" showIcon title={errorMessage(preview.error)} style={{ margin: 12 }} />
                ) : !preview.data ? (
                  <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
                ) : (
                  <>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(5,5,5,0.06)' }}>
                      <Typography.Title level={5} style={{ margin: 0 }}>{preview.data.subject || <Typography.Text type="secondary">(no subject)</Typography.Text>}</Typography.Title>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        <b>{preview.data.from}</b> to {preview.data.to}
                        {!preview.data.contact && ' · sample person'}
                      </Typography.Text>
                    </div>
                    <Spin spinning={preview.isFetching} delay={300}>
                      <div style={{ padding: '0 12px' }}><MailBody html={preview.data.html} /></div>
                    </Spin>
                  </>
                )}
              </Card>
              <Divider style={{ margin: '16px 0 12px' }} />
              <Space.Compact style={{ width: '100%' }}>
                <Input aria-label="Send a test to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@company.com" />
                <Button icon={<SendOutlined />} loading={test.isPending} disabled={!testTo.trim()} onClick={sendTest}>Send test</Button>
              </Space.Compact>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>A test is sent as the agent, without tracking, and isn't logged on any contact.</Typography.Text>
            </div>
          </Col>
        )}
      </Row>
    </Drawer>
  );
}
